import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Calendar component for displaying attendance by month
 *
 * @param {Object} props
 * @param {Date} props.currentMonth - The current month being displayed
 * @param {Date} props.selectedDate - The currently selected date
 * @param {Array} props.attendanceRecords - Array of attendance records
 * @param {Function} props.handlePreviousMonth - Function to navigate to previous month
 * @param {Function} props.handleNextMonth - Function to navigate to next month
 * @param {Function} props.handleSelectDate - Function to select a specific date
 */
const AttendanceCalendar = ({
    currentMonth,
    selectedDate,
    attendanceRecords,
    handlePreviousMonth,
    handleNextMonth,
    handleSelectDate
}) => {
    // Get attendance status for a specific date
    const getAttendanceStatus = (date) => {
        const dateStr = new Date(date).toISOString().split('T')[0];
        const record = attendanceRecords.find(rec => {
            const recordDate = new Date(rec.date).toISOString().split('T')[0];
            return recordDate === dateStr;
        });

        if (!record) return null;
        return record.present;
    };

    // Generate calendar for current month
    const generateCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        // Get the first day of the month
        const firstDay = new Date(year, month, 1);
        // Get the last day of the month
        const lastDay = new Date(year, month + 1, 0);

        // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
        const firstDayOfWeek = firstDay.getDay();

        // Generate an array of days for the calendar
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push({date: null, empty: true});
        }

        // Add days of the month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const date = new Date(year, month, i);
            const status = getAttendanceStatus(date);
            days.push({date, empty: false, status});
        }

        return days;
    };

    return (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-between items-center">
                <h2 className="text-lg leading-6 font-medium text-gray-900">Attendance Calendar</h2>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handlePreviousMonth}
                        className="p-1 rounded-full hover:bg-gray-200"
                    >
                        <ChevronLeft size={20} className="text-gray-600"/>
                    </button>
                    <span className="text-sm font-medium">
                        {currentMonth.toLocaleString('default', {
                            month: 'long',
                            year: 'numeric'
                        })}
                    </span>
                    <button
                        onClick={handleNextMonth}
                        className="p-1 rounded-full hover:bg-gray-200"
                    >
                        <ChevronRight size={20} className="text-gray-600"/>
                    </button>
                </div>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                {attendanceRecords.length === 0 ? (
                    <div className="text-center py-8">
                        <Calendar
                            className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No
                            attendance records</h3>
                        <p className="text-gray-500">
                            There are no attendance records available for this
                            class yet.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Calendar header */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day}
                                     className="text-center text-sm font-medium text-gray-500">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar grid */}
                        <div className="grid grid-cols-7 gap-1">
                            {generateCalendar().map((day, index) => (
                                <div
                                    key={index}
                                    className={`
                                        p-2 h-14 border rounded-md transition
                                        ${day.empty ? 'border-transparent' : 'cursor-pointer hover:border-indigo-300'}
                                        ${day.date && day.date.toISOString().split('T')[0] === selectedDate?.toISOString().split('T')[0] ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'}
                                        ${day.status === true ? 'bg-green-50' : day.status === false ? 'bg-red-50' : ''}
                                    `}
                                    onClick={() => day.date && handleSelectDate(day.date)}
                                >
                                    {!day.empty && (
                                        <>
                                            <div className="flex justify-between items-start">
                                                <span className={`text-sm font-medium ${day.status === true ? 'text-green-800' : day.status === false ? 'text-red-800' : 'text-gray-700'}`}>
                                                    {day.date.getDate()}
                                                </span>
                                                {day.status !== null && (
                                                    <div className={`h-2 w-2 rounded-full ${day.status ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Calendar legend */}
                        <div className="mt-4 flex justify-center space-x-6">
                            <div className="flex items-center">
                                <div className="h-3 w-3 bg-green-500 rounded-full mr-1"></div>
                                <span className="text-xs text-gray-600">Present</span>
                            </div>
                            <div className="flex items-center">
                                <div className="h-3 w-3 bg-red-500 rounded-full mr-1"></div>
                                <span className="text-xs text-gray-600">Absent</span>
                            </div>
                            <div className="flex items-center">
                                <div className="h-3 w-3 bg-gray-200 rounded-full mr-1"></div>
                                <span className="text-xs text-gray-600">No Record</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AttendanceCalendar;